/* ==========================================================================
   SIGNATURE — theme runtime
   Core interactions run on vanilla JS + IntersectionObserver so the theme is
   fast and works with zero dependencies. GSAP (if loaded) enhances the hero,
   parallax and horizontal scenes progressively.
   ========================================================================== */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion:reduce)').matches;
  var hasGSAP = typeof window.gsap !== 'undefined';
  var fine = window.matchMedia('(hover:hover) and (pointer:fine)').matches;

  /* ---- Scroll reveals (works everywhere) -------------------------------- */
  function initReveals() {
    var els = document.querySelectorAll('[data-reveal],[data-rise],.split');
    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('on'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('on'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.15 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---- Luxury cursor ----------------------------------------------------- */
  function initCursor() {
    if (!fine || reduce) return;
    var dot = document.createElement('div');
    dot.className = 'cursor';
    document.body.appendChild(dot);
    var x = 0, y = 0, cx = 0, cy = 0;
    document.addEventListener('mousemove', function (e) { x = e.clientX; y = e.clientY; });
    (function loop() {
      cx += (x - cx) * 0.18; cy += (y - cy) * 0.18;
      dot.style.transform = 'translate(' + cx + 'px,' + cy + 'px) translate(-50%,-50%)';
      requestAnimationFrame(loop);
    })();
    document.querySelectorAll('a,button,[data-cursor="lg"]').forEach(function (el) {
      el.addEventListener('mouseenter', function () { dot.classList.add('is-lg'); });
      el.addEventListener('mouseleave', function () { dot.classList.remove('is-lg'); });
    });
  }

  /* ---- Header state on scroll ------------------------------------------- */
  function initHeader() {
    var h = document.querySelector('[data-header]');
    if (!h) return;
    var last = 0;
    window.addEventListener('scroll', function () {
      var y = window.scrollY;
      h.classList.toggle('is-solid', y > 40);
      h.classList.toggle('is-up', y < last || y < 200);
      last = y;
    }, { passive: true });
  }

  /* ---- Reading progress (articles) -------------------------------------- */
  function initProgress() {
    var bar = document.querySelector('[data-progress]');
    var body = document.querySelector('[data-article-body]');
    if (!bar || !body) return;
    function set() {
      var r = body.getBoundingClientRect();
      var total = r.height - window.innerHeight;
      var passed = Math.min(Math.max(-r.top, 0), Math.max(total, 1));
      bar.style.transform = 'scaleX(' + (passed / Math.max(total, 1)) + ')';
    }
    window.addEventListener('scroll', set, { passive: true });
    window.addEventListener('resize', set);
    set();
  }

  /* ---- Table of contents active state ----------------------------------- */
  function initTOC() {
    var links = document.querySelectorAll('[data-toc] a');
    if (!links.length || !('IntersectionObserver' in window)) return;
    var map = {};
    links.forEach(function (l) {
      var t = document.querySelector(l.getAttribute('href'));
      if (t) map[t.id] = l;
    });
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          links.forEach(function (l) { l.classList.remove('is-active'); });
          if (map[e.target.id]) map[e.target.id].classList.add('is-active');
        }
      });
    }, { rootMargin: '-45% 0px -50% 0px' });
    Object.keys(map).forEach(function (id) {
      var el = document.getElementById(id); if (el) io.observe(el);
    });
  }

  /* ---- Accordions -------------------------------------------------------- */
  function initAccordions() {
    document.querySelectorAll('[data-accordion] > [data-acc-item]').forEach(function (item) {
      var btn = item.querySelector('[data-acc-trigger]');
      var panel = item.querySelector('[data-acc-panel]');
      if (!btn || !panel) return;
      btn.addEventListener('click', function () {
        var open = item.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open);
        panel.style.maxHeight = open ? panel.scrollHeight + 'px' : '';
      });
    });
  }

  /* ---- Marquee (duplicate track, CSS-driven or GSAP) -------------------- */
  function initMarquee() {
    document.querySelectorAll('[data-marquee]').forEach(function (track) {
      var clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.parentNode.appendChild(clone);
    });
  }

  /* ---- GSAP enhancement layer ------------------------------------------- */
  function initGSAP() {
    if (!hasGSAP || reduce) return;
    var gsap = window.gsap;
    if (window.ScrollTrigger) gsap.registerPlugin(window.ScrollTrigger);

    // Parallax on flagged media
    document.querySelectorAll('[data-parallax]').forEach(function (el) {
      var depth = parseFloat(el.getAttribute('data-parallax')) || 0.12;
      var img = el.querySelector('img') || el;
      gsap.fromTo(img, { yPercent: -depth * 100 }, {
        yPercent: depth * 100, ease: 'none',
        scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true }
      });
    });

    // Hero mouse perspective
    document.querySelectorAll('[data-tilt]').forEach(function (stage) {
      var layers = stage.querySelectorAll('[data-tilt-layer]');
      stage.addEventListener('mousemove', function (e) {
        var r = stage.getBoundingClientRect();
        var mx = (e.clientX - r.left) / r.width - 0.5;
        var my = (e.clientY - r.top) / r.height - 0.5;
        layers.forEach(function (l) {
          var d = parseFloat(l.getAttribute('data-tilt-layer')) || 12;
          gsap.to(l, { x: mx * d, y: my * d, duration: 0.9, ease: 'power2.out' });
        });
      });
      stage.addEventListener('mouseleave', function () {
        layers.forEach(function (l) { gsap.to(l, { x: 0, y: 0, duration: 1.1, ease: 'power2.out' }); });
      });
    });

    // Horizontal scroll scenes
    document.querySelectorAll('[data-horizontal]').forEach(function (scene) {
      var track = scene.querySelector('[data-horizontal-track]');
      if (!track || !window.ScrollTrigger) return;
      var dist = track.scrollWidth - scene.offsetWidth;
      if (dist <= 0) return;
      gsap.to(track, {
        x: -dist, ease: 'none',
        scrollTrigger: { trigger: scene, start: 'top top', end: '+=' + dist, scrub: 1, pin: true, invalidateOnRefresh: true }
      });
    });
  }

  /* ---- Predictive search ------------------------------------------------- */
  function initSearch() {
    var form = document.querySelector('[data-predictive]');
    if (!form) return;
    var input = form.querySelector('input[type="search"]');
    var out = form.querySelector('[data-predictive-results]');
    if (!input || !out) return;
    var t;
    input.addEventListener('input', function () {
      clearTimeout(t);
      var q = input.value.trim();
      if (q.length < 2) { out.innerHTML = ''; out.hidden = true; return; }
      t = setTimeout(function () {
        var url = '/search/suggest?q=' + encodeURIComponent(q) +
          '&resources[type]=article,product,page&resources[limit]=6&section_id=predictive-search';
        fetch(url).then(function (r) { return r.text(); }).then(function (html) {
          out.innerHTML = html; out.hidden = false;
        }).catch(function () {});
      }, 220);
    });
  }

  /* ---- Boot -------------------------------------------------------------- */
  function boot() {
    initReveals(); initCursor(); initHeader(); initProgress();
    initTOC(); initAccordions(); initMarquee(); initSearch(); initGSAP();
    document.documentElement.classList.add('is-ready');
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Re-init reveals when Theme Editor injects/reloads a section
  document.addEventListener('shopify:section:load', function () { initReveals(); initAccordions(); initGSAP(); });
})();
